import { Feature, Map } from 'ol';
import { Polyline } from 'ol/format';
import { Geometry, LineString, Point, Circle } from 'ol/geom';
import { Style as Vector_style, Icon as Vector_icon, Style } from 'ol/style';

import { titleCase } from '../../util/general_util';
import { DEFAULT_LOCKED_STOP_ICON_src, DEFAULT_LOCKED_STOP_ICON_W_POIS_src, DEFAULT_UNLOCKED_STOP_ICON_src, DEFAULT_UNLOCKED_STOP_ICON_W_POIS_src, errorRouteIconStyle, errorRouteStyle, potentialStayStyle, stayMarkerStyle, stayMarkerStyle_favorited, stayMarkerStyle_s2n } from './static_defs';
import { get_camp_addr, getStopTitles_fromLegs, getWarningText_stopNames } from '../util/helper_functions';
import { Campground_Data, POI_Data, Trip_Cluster_v2, Trip_leg_v2, Trip_v2 } from '../../util/tripPlanner_types';
import { clearLayer, enlargeIcon, fitBounds, getRouteBounds, shrinkIcon } from './helper_functions';
import { Coordinate } from 'ol/coordinate';
import { boundingExtent, Extent } from 'ol/extent';
import { get_favoritedCamps } from '../api_utils';

export function remove_stay_pin(campID:number) {
	const pins:Feature[] = window.clusterMarkerLayer.getSource().getFeatures();
	for (let i:number = 0; i < pins.length; i++) {
		const properties:{[key:string]:any} = pins[i].getProperties();
		if (properties.campID == campID) {
			window.clusterMarkerLayer.getSource().removeFeature(pins[i]);
			break;
		};
	};
};

export function update_stayPin_opacity(hiddenCampIDs:number[]) {
	const stayPins: Array<Feature> = window.clusterMarkerLayer.getSource().getFeatures();
	for (let i:number = 0; i < stayPins.length; i++) {
		if (hiddenCampIDs.includes(stayPins[i].getProperties().campID)) {
			setMarkerOpacity(stayPins[i], stayPins[i].getStyle() as Style, 0.15);
		} else {
			setMarkerOpacity(stayPins[i], stayPins[i].getStyle() as Style, 1);
		};
	};
};

function setMarkerOpacity(marker:Feature, style:Vector_style, opacity:number) {
	marker.setStyle(style.clone());
	(marker.getStyle() as Vector_style).getImage().setOpacity(opacity);

	marker.changed();
};

function displayRouteWarnings(map:Map, directions:any):void {
    let issueRoutes:Feature[] = [];

    if ('routeIssues' in directions.routes[0]) {
		let routeIssues:any = directions.routes[0].routeIssues;
		for (let i:number = 0; i < routeIssues.length; i++) {
			let issuePolyline = directions.routes[0].geometry.coordinates.slice(routeIssues[i].startPointIndex, routeIssues[i].endPointIndex + 1);
			let issueRoute:Geometry = new LineString(issuePolyline);
			let routeFeature:Feature = new Feature({
				type: 'route',
				geometry: issueRoute
			});
			let warningText:string;
			if (routeIssues[i].sectionType == "TRAVEL MODE") {
				warningText = "Red portion of route may not be RV safe."
			} else {
				warningText = `Couldn't avoid ${titleCase(routeIssues[i].sectionType)}.<br>See red portion of route.`;
			};

			let midPoint = (issueRoute as LineString).getCoordinateAt(0.5);
			let iconFeature = new Feature(new Point(midPoint));
			iconFeature.setStyle(errorRouteIconStyle);

			routeFeature.setStyle(errorRouteStyle);
			iconFeature.setProperties({
				type:"routeWarning",
				warningText: warningText
			})
			issueRoutes.push(routeFeature);
			issueRoutes.push(iconFeature);
		};
	};
	if ('routeNotices' in directions.routes[0]) {
		let routeNotices:any = directions.routes[0].routeNotices;

		for (let i:number = 0; i < routeNotices.length; i++) {
			const segment_geo = new LineString(directions.routes[0].segments[routeNotices[i].section].geo)
			let midPt = segment_geo.getCoordinateAt(0.5);
			let iconFeature = new Feature(new Point(midPt));

			let stopTitles:Array<string> = getStopTitles_fromLegs()
			let warningText_stopNames = getWarningText_stopNames(routeNotices[i].section,stopTitles)
			iconFeature.setStyle(errorRouteIconStyle);
			iconFeature.setProperties({
				type:"routeWarning",
				warningText: routeNotices[i].warning +  warningText_stopNames
			});
			issueRoutes.push(iconFeature);
		};
	};

    if (map == window.mainTripPlannerMap) {
		window.routeLayer.getSource().addFeatures(issueRoutes);
	};
};

export function displayRoute(map:Map, directions:any):void {
    const polyline = directions.routes[0].geometry;
    let route:Geometry|Polyline;
    if (typeof polyline == "string") {
        route = new Polyline().readGeometry(polyline);
    } else {
        route = new LineString(polyline.coordinates);
    };
    const routeFeature = new Feature({
        type: 'route',
        geometry: route
    });

    displayRouteWarnings(map, directions);

    if (map == window.mainTripPlannerMap) {
		window.routeLayer.getSource().addFeature(routeFeature);
	};
};

export function center_map(map:Map, trip:Trip_v2) {
    const bounds = getRouteBounds(trip);
	fitBounds(map, bounds);
};

export function remove_POI_pins(removeAll:boolean, type_ids:number[]) {
	if (removeAll) {
		clearLayer(window.poiMarkerLayer);
		return;
	}
	const features:Feature[] = window.poiMarkerLayer.getSource().getFeatures();
	for (let i:number = 0; i < features.length; i++) {
		const properties = features[i].getProperties();
		if (type_ids.includes(properties.poi_type)) {
			window.poiMarkerLayer.getSource().removeFeature(features[i]);
		};
	};
};

export function remove_pois_on_route(poi_typeID:number) {
	const features = window.poiOnRouteMarkerLayer.getSource().getFeatures();
	for (let i:number = 0; i < features.length; i++) {
		const properties:{[key:string]:any} = features[i].getProperties();
		if (properties.poi_type == poi_typeID) {
			window.poiOnRouteMarkerLayer.getSource().removeFeature(features[i]);
		};
	};
};

export function remove_openRoads_on_route() {
	const features = window.poiOnRouteMarkerLayer.getSource().getFeatures();
	for (let i:number = 0; i < features.length; i++) {
		const properties:{[key:string]:any} = features[i].getProperties();
		if (properties.openRoads_poi) {
			window.poiOnRouteMarkerLayer.getSource().removeFeature(features[i]);
		};
	};
}

export function add_pois_on_route(pois:POI_Data[],openRoads=false) {
	const newMarkers:Feature[] = [];
	for (let i:number = 0; i < pois.length; i++) {
		const marker = new Feature(new Point([pois[i].loc.longitude, pois[i].loc.latitude]));
		if (openRoads && pois[i].openRoads_poi) {
			marker.setStyle(new Vector_style({
				image: new Vector_icon({
					src: "/static/images/icons/poi_icons/poi_type_36.png",
					scale: 0.25
				})
			}));
			marker.setProperties({
				id: pois[i].id,
				icon: pois[i].icon,
				loc: pois[i].loc,
				type: "poiPin_onRoute",
				poi_type: pois[i].poi_typeID,
				name: pois[i].name,
				street_address: pois[i].street_address,
				city: pois[i].city,
				administrative_area_level_1:pois[i].administrative_area_level_1,
				info_phone: pois[i].info_phone,
				openRoads_brand: pois[i].openRoads_brand,
				openRoads_poi: true,
			});
		} else {
			marker.setStyle(new Vector_style({
				image: new Vector_icon({
					src: pois[i].icon,
					scale: 1
				})
			}));
			marker.setProperties({
				id: pois[i].id,
				icon: pois[i].icon,
				loc: pois[i].loc,
				type: "poiPin_onRoute",
				poi_type: pois[i].poi_typeID,
				name: pois[i].name,
				street_address: pois[i].street_address,
				city: pois[i].city,
				administrative_area_level_1:pois[i].administrative_area_level_1,
				info_phone: pois[i].info_phone,
				openRoads_brand: pois[i].openRoads_brand,
				openRoads_poi: false,
			});
		}
		newMarkers.push(marker);
	};
	if (window.poisOnRouteActive) {
		window.poiOnRouteMarkerLayer.getSource().addFeatures(newMarkers);
	};
};
export function display_POI_pins(POIs:POI_Data[], stopID:string, selected_poi_types:number[]) {
	const newMarkers:Feature[] = [];
	for (let i:number = 0; i < POIs.length; i++) {
		if (!selected_poi_types.includes(POIs[i].poi_typeID)) continue;

		const coords = POIs[i].loc;
		const poiMarker = new Feature(new Point([coords.longitude, coords.latitude]));
		poiMarker.setStyle(new Vector_style({
			image: new Vector_icon({
				src: POIs[i].icon,
				scale: 0.75
			})
		}));
		poiMarker.setProperties({
			stopID: stopID,
			type: "poiPin",
			poi_type: POIs[i].poi_typeID,
			poiID: POIs[i].id,

			id: POIs[i].id,
			icon: POIs[i].icon,
			loc: POIs[i].loc,
			name: POIs[i].name,
			street_address: POIs[i].street_address,
			city: POIs[i].city,
			administrative_area_level_1:POIs[i].administrative_area_level_1,
			info_phone: POIs[i].info_phone
		});
		newMarkers.push(poiMarker);
	};
	window.poiMarkerLayer.getSource().addFeatures(newMarkers);

};

export function displayStayPins(trip:Trip_v2) {
	let newMarkers: Array<Feature> = [];
	let i:number, j:number, k:number;
	for (i = 0; i < trip.legs.length; i ++) {
        const leg:Trip_leg_v2 = trip.legs[i];
        for (j = 0; j < leg.clusters.length; j++) {
            const stop:Trip_Cluster_v2 = leg.clusters[j];
			for (k = 0; k < stop.selectedCamps.length; k++) {
				const lonLat = [stop.selectedCamps[k].camp.longitude, stop.selectedCamps[k].camp.latitude]
				const stayMarker = new Feature(new Point(lonLat));
				stayMarker.setStyle(stayMarkerStyle);
				stayMarker.setProperties({
					campID:stop.selectedCamps[k].camp.campID
				})
				newMarkers.push(stayMarker);
			};
		};
	};
	window.stayMarkerLayer.getSource().addFeatures(newMarkers);
};

export function displayStopPins(trip:Trip_v2) {
    let newMarkers: Array<Feature> = [];
    let i:number, j:number, k:number;
	for (i = 0; i < trip.legs.length; i ++) {
        const leg:Trip_leg_v2 = trip.legs[i];
        for (j = 0; j < leg.clusters.length; j++) {
            const stop:Trip_Cluster_v2 = leg.clusters[j];
            if (stop.hideStopPin == true) continue;
            const is_locked:boolean = leg.lockedStops.includes(j);
            const hasPOIs = stop.POIs != undefined && stop.POIs.length > 0;

            let icon_src:string;
            if (is_locked && hasPOIs) {
				icon_src = DEFAULT_LOCKED_STOP_ICON_W_POIS_src;
			} else if (hasPOIs) {
				icon_src = DEFAULT_UNLOCKED_STOP_ICON_W_POIS_src;
			} else if (is_locked) {
				icon_src = DEFAULT_LOCKED_STOP_ICON_src;
			} else {
				icon_src = DEFAULT_UNLOCKED_STOP_ICON_src;
			};

            const lonLat = [stop.coords.longitude, stop.coords.latitude];

            const stopMarker:Feature = new Feature(new Point(lonLat))
			stopMarker.setStyle(new Vector_style({
				image: new Vector_icon({
					src: icon_src
				})
			}));
            stopMarker.setProperties({
                stopID: stop.stopID,
                type: "stopMarker"
            });
            newMarkers.push(stopMarker);
        };
    };
    window.mainMapMarkerLayer.getSource().addFeatures(newMarkers);
};

export function zoomToStop(map:Map, stop:Trip_Cluster_v2, is_locked:boolean) {
	const lonLat:Coordinate = [stop.coords.longitude, stop.coords.latitude];
	let i:number;
	let max:number = 0;
	let lats:number[] = [], lons:number[] = []
	for (i = 0; i < stop.altStops.length; i++) {
		lats.push(stop.altStops[i].camp.latitude);
		lons.push(stop.altStops[i].camp.longitude);
		let altStop:Campground_Data = stop.altStops[i];
		let latDiff:number = Math.abs(stop.coords.latitude - altStop.camp.latitude);
		let lonDiff:number = Math.abs(stop.coords.longitude - altStop.camp.longitude);
		let diff:number = Math.sqrt(latDiff**2 + lonDiff**2);
		if (diff > max) max = diff;
	};

	let max_lon = Math.max(...lons);
	let min_lon = Math.min(...lons);
	let max_lat = Math.max(...lats);
	let min_lat = Math.min(...lats);

	// /* adjust to fit on map better w/ stop popout */
	// min_lon -= 0.75;
	// max_lon -= 0.75;
	// /****************** */

	const bounds:Extent = boundingExtent([
		[max_lon,max_lat],
		[min_lon, min_lat],
		[max_lon, min_lat],
		[min_lon, max_lat]
	])

	if (max < 0.1) max = 0.1;

	clearLayer(window.mainMapMarkerLayer);
	clearLayer(window.zoomedClusterMarkerLayer_agStops);
	clearLayer(window.zoomedClusterMarkerLayer_userStops);

	const stopCircle:Feature = new Feature(new Circle(lonLat, max*1.2));

	if (is_locked) {
		window.zoomedClusterMarkerLayer_userStops.getSource().addFeature(stopCircle);
	} else {
		window.zoomedClusterMarkerLayer_agStops.getSource().addFeature(stopCircle);
	};


	if (stop.altStops.length > 0) {
		fitBounds(map, bounds);
	};
};

export async function display_potential_stay_pins(stop:Trip_Cluster_v2) {
	if (stop.altStops == undefined || stop.altStops == null) return;

	if (!window.favoritedCamps) {
		window.favoritedCamps = await get_favoritedCamps(null).catch(e => {console.error(e)});
	};

	let newMarkers:Feature[] = [];
	for (let i:number = 0; i < stop.altStops.length; i++) {
		const camp = stop.altStops[i];
		const addr:string = get_camp_addr(camp);
		const dist:number|null = camp.dist != undefined? camp.dist: null;

		const marker:Feature = new Feature(new Point([camp.camp.longitude, camp.camp.latitude]));
		if (window.favoritedCamps && window.favoritedCamps.includes(camp.camp.campID)) {
			marker.setStyle(stayMarkerStyle_favorited);
		} else if (camp.camp.s2n_id != null) {
			marker.setStyle(stayMarkerStyle_s2n);
		} else {
			marker.setStyle(potentialStayStyle);
		};
		marker.setProperties({
			campID: camp.camp.campID,
			type: "stayPin",
			camp_name: camp.camp.camp_name,
			genie_score: camp.camp.genie_score,
			genie_match: camp.camp.genieMatch,
			// leg: legIndex,
			// stop: stopIndex,
			stopID: stop.stopID,
			s2n_id: camp.camp.s2n_id,
			campPageURL: `/rv-campgrounds/${camp.camp.administrative_area_level_1_long}/${camp.camp.city}/${camp.camp.camp_name}`.toLowerCase().replace(" ","-"),
			addr: addr,
			dist: dist
		});
		newMarkers.push(marker);
	};

	zoomToStop(window.mainTripPlannerMap, stop, stop.fromUserQuery);

	clearLayer(window.clusterMarkerLayer);
	window.clusterMarkerLayer.getSource().addFeatures(newMarkers);
};